import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { num } from '../../format';
import { useNavigation } from '../layout/navigation';
import { LowStockPanel } from './LowStockPanel';
import { LocationReportPanel } from './LocationReportPanel';
import { SerialLookup } from './SerialLookup';
import { ShortagePanel } from './ShortagePanel';
import { StockTable } from './StockTable';
import { checkIntegrity, lowStock, shortages, type IntegrityReport } from './api';

const TABS = ['table', 'locations', 'shortage', 'low', 'serials', 'integrity'] as const;
type Tab = (typeof TABS)[number];

/**
 * The warehouse: what there is, what is missing, and whether the books add up.
 *
 * Five questions asked at different moments, so five tabs rather than five
 * blocks competing for the top of one page. Two of them are about *missing*
 * and are deliberately kept apart: "running out" is a standing opinion about a
 * part — its minimum level — while "short" is arithmetic over what the running
 * projects actually plan to build. A part can sit comfortably above its minimum
 * and still be two hundred short because a batch of boards was planned last
 * week.
 *
 * The integrity check is deliberately a button rather than a background job for
 * now. It answers the invariant the ledger is built around — sum(movements)
 * equals the balances equals the component totals — and a person pressing it
 * gets an answer they can act on, where a silent nightly job produces a log
 * nobody reads.
 */
export function StockPage() {
  const { t } = useTranslation();
  const { open } = useNavigation();
  const [search, setSearch] = useSearchParams();
  const requestedTab = search.get('tab');
  const tab: Tab = TABS.includes(requestedTab as Tab) ? requestedTab as Tab : 'table';
  const [report, setReport] = useState<IntegrityReport | null>(null);

  const low = useQuery({ queryKey: ['stock', 'low'], queryFn: lowStock });
  // Fetched here rather than only inside the tab so the count on the tab is
  // there before it is opened — a shortage nobody is told about is one nobody
  // acts on.
  const short = useQuery({ queryKey: ['projects', 'shortages'], queryFn: shortages });
  const verify = useMutation({ mutationFn: checkIntegrity, onSuccess: setReport });

  const count = (key: Tab) => {
    if (key === 'low') return low.data?.length ?? 0;
    if (key === 'shortage') return short.data?.length ?? 0;
    return 0;
  };

  return (
    <div className="stock-page">
      <div className="tabs" role="tablist">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={tab === key ? 'tabs__tab is-active' : 'tabs__tab'}
            onClick={() => {
              const next = new URLSearchParams(search);
              if (key === 'table') next.delete('tab'); else next.set('tab', key);
              setSearch(next, { replace: true });
            }}
          >
            {t(`stock.tab.${key}`)}
            {count(key) > 0 && <span className="tabs__count">{count(key)}</span>}
          </button>
        ))}
      </div>

      {tab === 'table' && <StockTable />}
      {tab === 'locations' && <LocationReportPanel />}
      {tab === 'shortage' && <ShortagePanel />}
      {tab === 'serials' && <SerialLookup />}

      {tab === 'low' && <LowStockPanel />}

      {tab === 'integrity' && (
        <section>
          <button type="button" onClick={() => verify.mutate()} disabled={verify.isPending}>
            {t('stock.integrity')}
          </button>
          {report?.ok && <p className="stock__ok">{t('stock.integrityOk')}</p>}
          {report && !report.ok && (
            <>
              <p className="panel__error">{t('stock.integrityBad', { n: report.problems.length })}</p>
              <ul className="stock__history">
                {report.problems.map((problem, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      className="link components__code"
                      onClick={() => open('component', problem.component_id)}
                    >
                      {problem.component_id}
                    </button>
                    <span>{num(problem.ledger)}</span>
                    <span>{'≠'}</span>
                    <strong>{num(problem.recorded)}</strong>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}
    </div>
  );
}
